import { useState, useEffect, useRef } from 'react';

import classes from "./BookingDetailsCard.module.css";
import { useBookingUpdate } from '../../../hooks/useBookingUpdate';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { SmallModal } from '../../../components/UI/SmallModal/SmallModal';
import { useCreateUserReviews } from '../../../hooks/reviews/useCreateUserReviews';
import { useCreateHostStudioReviews } from '../../../hooks/reviews/useCreateHostStudioReviews';
import { isAlreadyReviewedError } from '../../../hooks/reviews/reviewErrorUtils';
import { useQuery, gql } from '@apollo/client';
import BookingDetailsCardSkeleton from './BookingDetailsCardSkeleton';
import SaveNotification from '../../../components/UI/SaveNotification/SaveNotification';
import { markBookingAsViewed } from '../../../util/bookingViewedTracker';
import { formatBerlinDateTimeRange, isCompletedBookingStatus } from '../../../util/bookingDateTime';
import { buildCoarseLocationText, buildExactLocationText, canShowExactLocationForStatus } from '../../../util/locationPrivacy';
import noUserImage from "../../../public/fakeUser/noUser.webp";
import noImage from "../../../public/noImage.webp";
import { notifyErrorToast } from '../../../util/notifyActionError';

const GET_BOOKING_DETAILS = gql`
  query GetBookingDetails($id: ID!) {
    getBooking(id: $id) {
      id
      guestReviewed
      hostReviewed
      reviews {
        items {
          id
          reviewerId
          revieweeId
          reviewType
        }
      }
    }
  }
`;

export const BookingDetailsCard = ({ bookingData, packageData, fromCreatorPage = false, fromMessagesPage = false, loading = false, suppressInlineActions = false }) => {
    const { user } = useSelector((state) => state?.auth);
    const userId = user?.id || user?._id;

    const [selectedStars, setSelectedStars] = useState(0);
    const [guestReviewText, setGuestReviewText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isUserReviewSubmitting, setIsUserReviewSubmitting] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [bookingReviewSubmittedLocally, setBookingReviewSubmittedLocally] = useState(false);
    const [creatorReviewSubmittedLocally, setCreatorReviewSubmittedLocally] = useState(false);
    const bookingReviewSubmitInFlightRef = useRef(false);
    const creatorReviewSubmitInFlightRef = useRef(false);
    const isHostViewer = bookingData?.studioOwnerID === userId;
    const hasExactLocation = (isHostViewer || canShowExactLocationForStatus(bookingData?.status)) && !!bookingData?.exactLocation;
    const exactAddressText = buildExactLocationText(bookingData?.exactLocation || {});
    const coarseAddressText = buildCoarseLocationText(bookingData?.Studio) || bookingData?.Studio?.city || '';
    const addressText = hasExactLocation && exactAddressText
        ? exactAddressText
        : coarseAddressText;
    // const [moreReviews, setMoreReviews] = useState(true); 

    // Mark booking as viewed when component mounts or booking data changes
    useEffect(() => {
        if (bookingData?.id && bookingData?.status && userId) {
            markBookingAsViewed(bookingData.id, bookingData.status, userId);
        }
    }, [bookingData?.id, bookingData?.status, userId]);

    const { submitUserReview } = useCreateUserReviews();
    const { submitStudioReview, isSubmitting: isStudioReviewSubmitting } = useCreateHostStudioReviews();

    const { updateBookingStatus, isLoading: isBookingUpdateLoading } = useBookingUpdate();

    // Check if booking start time has passed
    const hasBookingTimePassed = () => {
        if (!bookingData?.startDateTime) return false;
        const bookingStartTime = new Date(bookingData.startDateTime);
        const now = new Date();
        return now > bookingStartTime;
    };

    const { data: bookingDetails, loading: reviewsLoading } = useQuery(GET_BOOKING_DETAILS, {
        variables: { id: bookingData?.id },
        skip: !bookingData?.id,
        fetchPolicy: 'cache-and-network'
    });

    const bookingReviewState = bookingDetails?.getBooking || bookingData;
    const hasSubmittedReviewForRole = fromCreatorPage
        ? bookingReviewState?.hostReviewed === true
        : bookingReviewState?.guestReviewed === true;
    const hasReviewRecordForRole = bookingReviewState?.reviews?.items?.some(review => {
        if (fromCreatorPage) {
            return review.reviewerId === bookingData?.Studio?.User?.id && 
                   review.revieweeId === bookingData?.userID;
        } else {
            return review.reviewerId === bookingData?.userID && 
                   review.revieweeId === bookingData?.Studio?.User?.id;
        }
    }) || false;
    const hasLocallySubmittedReviewForRole = fromCreatorPage
        ? creatorReviewSubmittedLocally
        : bookingReviewSubmittedLocally;
    const hasExistingReview = hasLocallySubmittedReviewForRole || hasSubmittedReviewForRole || hasReviewRecordForRole;

    const [selectedStudioStars, setSelectedStudioStars] = useState(0);
    const [selectedCommunicationStars, setSelectedCommunicationStars] = useState(0);

    const [studioReviewText, setStudioReviewText] = useState('');

    const [modal, setModal] = useState({ 
        isOpen: false, 
        type: null,
        message: '', 
        action: null,
        isRateBookingHostOpen: false
    });
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    const clearRatingModalNavigationState = () => {
        if (!location.state?.openRatingModal) return;

        navigate(`${location.pathname}${location.search || ''}${location.hash || ''}`, {
            replace: true,
            state: {
                ...location.state,
                openRatingModal: false
            }
        });
    };

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 720);
        };

        checkIfMobile();

        window.addEventListener('resize', checkIfMobile);

        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    useEffect(() => {
        setBookingReviewSubmittedLocally(false);
        setCreatorReviewSubmittedLocally(false);
        bookingReviewSubmitInFlightRef.current = false;
        creatorReviewSubmitInFlightRef.current = false;
    }, [bookingData?.id]);

    const handleClose = () => {
        clearRatingModalNavigationState();
        setCurrentPage(1);
        setSelectedStars(0);
        setSelectedStudioStars(0);
        setSelectedCommunicationStars(0);
        setStudioReviewText('');
        setGuestReviewText('');
        setModal({ isOpen: false, type: null, message: '', action: null, isRateHostOpen: false, isRateBookingHostOpen: false });
    };

    const completeReviewSubmission = (markSubmitted) => {
        markSubmitted(true);
        setNotificationMessage('Bewertung abgeschickt');
        setShowNotification(true);
        handleClose();
    };
    
    const openModal = (type, options = {}) => {
        
        setModal({ 
            isOpen: true, 
            type, 
            message: options.message || '', 
            action: options.action || null,
            isRateHostOpen: options.isRateHostOpen || false,
            isRateBookingHostOpen: options.isRateBookingHostOpen || false
        });
    };

    // Auto-open rating modal if navigated from "Buchung bewerten" button
    useEffect(() => {
        if (!location.state?.openRatingModal || !bookingData || fromCreatorPage || reviewsLoading) {
            return;
        }

        // Check if booking time has passed and no existing review
        const timePassed = hasBookingTimePassed();
        if (timePassed && !hasExistingReview && bookingData?.status === "COMPLETED") {
            openModal('rate', { isRateBookingHostOpen: true });
        }

        clearRatingModalNavigationState();
    }, [
        location.state?.openRatingModal,
        location.pathname,
        location.search,
        location.hash,
        bookingData?.id,
        bookingData?.status,
        bookingData?.startDateTime,
        hasExistingReview,
        reviewsLoading,
        fromCreatorPage
    ]);

    useEffect(() => {
        if (!modal?.isOpen || modal?.type !== 'rate' || !hasExistingReview) return;
        if (bookingReviewSubmitInFlightRef.current || creatorReviewSubmitInFlightRef.current) return;

        handleClose();
    }, [modal?.isOpen, modal?.type, hasExistingReview, bookingData?.id]);

    const closeModal = () => {
        if (isMobile) {
            // Add slide-out class, then reset modal state after animation
            const modalContainer = document.querySelector(`.${classes.mobileModalContainer}`);
            if (modalContainer) {
                modalContainer.classList.remove(classes.mobileModalOpen);
                setTimeout(() => {
                    setSelectedStars(0);
                    setSelectedStudioStars(0);
                    setSelectedCommunicationStars(0);
                    setStudioReviewText('');
                    setGuestReviewText('');
                    setCurrentPage(1);
                    setModal({ isOpen: false, type: null, message: '', action: null, isRateHostOpen: false, isRateBookingHostOpen: false });
                }, 300); // Match the CSS transition duration
            }
        } else {
            // Desktop modal close (immediate)
            setSelectedStars(0);
            setSelectedStudioStars(0);
            setSelectedCommunicationStars(0);
            setStudioReviewText('');
            setGuestReviewText('');
            setCurrentPage(1);
            setModal({ isOpen: false, type: null, message: '', action: null, isRateHostOpen: false, isRateBookingHostOpen: false });
        }
    };

    const handleModalAction = async () => {
        const { action } = modal;
        // Don't close modal yet - wait for mutation to complete
        
        if (action === 'confirm') {
            await handleConfirmBooking();
        } else if (action === 'decline') {
            await handleDeclineBooking();
        }
    };

    const handleConfirmBooking = async () => {
        try {
            await updateBookingStatus(bookingData?.id, "CONFIRMED");
            setNotificationMessage('Buchung bestätigt');
            setShowNotification(true);
            handleClose(); // Close the modal after success
        } catch (error) {
            openModal('error', { 
                message: 'Es tut uns leid, aber es gab einen Fehler beim Bestätigen der Buchung. Bitte versuchen Sie es später erneut.' 
            });
        }
    };

    const handleDeclineBooking = async () => {
        try {
            await updateBookingStatus(bookingData?.id, "DECLINED");
            setNotificationMessage('Buchung abgelehnt');
            setShowNotification(true);
            handleClose(); // Close modal after success
            setTimeout(() => {
                navigate("/creator");
            }, 1500); // Navigate after showing notification briefly
        } catch (error) {
            openModal('error', { 
                message: 'Es tut uns leid, aber es gab einen Fehler beim Ablehnen der Buchung. Bitte versuchen Sie es später erneut.'
            });
        }
    };

    const generateICSFile = () => {
        // Validate that we have the required data
        if (!bookingData?.startDateTime || !bookingData?.endDateTime) {
            openModal('error', { 
                message: 'Datum und Zeit der Buchung sind nicht verfügbar.' 
            });
            return;
        }

        try {
            // Parse the dates directly as Date objects (same approach as formatDateTime)
            const startDate = new Date(bookingData.startDateTime);
            const endDate = new Date(bookingData.endDateTime);

            // Validate that the dates are valid
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                openModal('error', { 
                    message: 'Ungültiges Datum oder Zeit in der Buchung.' 
                });
                return;
            }

            const formatDateForICS = (date) => {
                return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            };

            const icsContent = `BEGIN:VCALENDAR
                VERSION:2.0
                BEGIN:VEVENT
                DTSTART:${formatDateForICS(startDate)}
                DTEND:${formatDateForICS(endDate)}
                SUMMARY:Termin bei ${bookingData?.User?.firstName} ${bookingData?.User?.lastName}
                LOCATION:${addressText}
                END:VEVENT
                END:VCALENDAR`;

            const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.setAttribute('download', `termin_${bookingData?.User?.firstName}_${bookingData?.User?.lastName}.ics`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            openModal('error', { 
                message: 'Es gab einen Fehler beim Erstellen der Kalenderdatei.' 
            });
        }
    };

    const openGoogleMaps = () => {
        if (!hasExactLocation || !exactAddressText) return;
        const encodedAddress = encodeURIComponent(exactAddressText);
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    };

    const formatDateTime = (startDate, endDate) => {
        return formatBerlinDateTimeRange(startDate, endDate) || {
            date: 'Datum nicht verfügbar',
            time: 'Zeit nicht verfügbar'
        };
    };

    const calculateDuration = (start, end) => {
        const startTime = new Date(start);
        const endTime = new Date(end);
        const durationHours = (endTime - startTime) / (1000 * 60 * 60);
        return durationHours;
    };

    const calculateHoursRemaining = (createdAt) => {
        if (!createdAt) return null;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        const hoursRemaining = Math.max(0, Math.ceil(24 - hoursPassed));
        return hoursRemaining;
    };

    const isBookingExpired = (createdAt) => {
        if (!createdAt) return false;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        return hoursPassed > 24;
    };

    const getStatusDisplay = () => {
        const status = bookingData?.status;
        const hostName = bookingData?.Studio?.User?.firstName || '';
        const guestName = bookingData?.User?.firstName || '';
        const cancelledBy = bookingData?.cancelledBy;
        const createdAt = bookingData?.createdAt;
        const hoursRemaining = calculateHoursRemaining(createdAt);
        const expired = isBookingExpired(createdAt);
        const completed = isCompletedBookingStatus(status);

        // Check if cancelled by guest or host
        // cancelledBy is an enum: "HOST" | "GUEST" | "SYSTEM"
        const cancelledByGuest = cancelledBy === "GUEST";
        const cancelledByHost = cancelledBy === "HOST";
        const cancelledBySystem = cancelledBy === "SYSTEM";

        if (completed) {
            return {
                message: '',
                statusText: 'Abgeschlossen',
                color: '#A1A7AE'
            };
        }

        if (status === "NO_SHOW") {
            return {
                message: fromCreatorPage 
                    ? `${guestName} ist nicht erschienen`
                    : 'Du bist nicht erschienen',
                statusText: 'Nicht erschienen',
                color: '#CE425D'
            };
        }

        if (status === "EXPIRED" || (expired && status === "PENDING")) {
            return {
                message: 'Die Anfrage ist abgelaufen',
                statusText: 'Anfrage abgelaufen',
                color: '#CE425D'
            };
        }

        if (status === "INQUIRY" || (status === "PENDING" && !expired && hoursRemaining === null)) {
            return {
                message: fromCreatorPage 
                    ? `Erkundigung von ${guestName}`
                    : `Du hast dich bei ${hostName} erkundigt`,
                statusText: 'Erkundigung',
                color: '#2788EE'
            };
        }

        if (status === "PENDING" && !expired) {
            return {
                message: fromCreatorPage 
                    ? `${guestName} hat bei dir angefragt`
                    : `Du hast bei ${hostName} angefragt`,
                statusText: `Angefragt: Läuft in ${hoursRemaining} ${hoursRemaining === 1 ? 'Stunde' : 'Stunden'} ab`,
                color: '#F7871E'
            };
        }

        if (status === "WITHDRAWN") {
            return {
                message: fromCreatorPage 
                    ? `${guestName} hat die Anfrage zurückgezogen`
                    : `Du hast die Anfrage zurückgezogen`,
                statusText: 'Anfrage zurückgezogen',
                color: '#CE425D'
            };
        }

        if (status === "DECLINED" || status === "REJECTED") {
            return {
                message: fromCreatorPage 
                    ? `Du hast die Buchungsanfrage abgelehnt`
                    : `${hostName} hat die Buchungsanfrage abgelehnt`,
                statusText: 'Anfrage abgelehnt',
                color: '#CE425D'
            };
        }

        if (status === "CONFIRMED") {
            return {
                message: fromCreatorPage 
                    ? `Du hast die Buchung bestätigt`
                    : `${hostName} hat die Buchungsanfrage bestätigt`,
                statusText: 'Buchung bestätigt',
                color: '#20BD13'
            };
        }

        if (status === "CANCELLED") {
            if (fromCreatorPage) {
                // Creator mode: Case 6 & 7
                if (cancelledByGuest) {
                    // Guest cancelled: "[Name des Entdeckers] hat die Buchung storniert" Status: "Vom Entdecker storniert"
                    return {
                        message: `${guestName} hat die Buchung storniert`,
                        statusText: 'Vom Entdecker storniert',
                        color: '#CE425D'
                    };
                } else if (cancelledByHost) {
                    // Creator cancelled: "Du hast die Buchung storniert" Status: "Vom Creator storniert"
                    return {
                        message: `Du hast die Buchung storniert`,
                        statusText: 'Von dir storniert',
                        color: '#CE425D'
                    };
                } else if (cancelledBySystem) {
                    // System cancelled
                    return {
                        message: 'Die Buchung wurde automatisch storniert',
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                } else {
                    // Fallback if cancelledBy is not available - assume creator cancelled
                    return {
                        message: `Du hast die Buchung storniert`,
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                }
            } else {
                // Entdecker mode: Case 6 & 7
                if (cancelledByHost) {
                    // Host cancelled: "[Name des Hosts] hat die Buchung storniert" Status: "Von [Name des Hosts] storniert"
                    return {
                        message: `${hostName} hat die Buchung storniert`,
                        statusText: `Von ${hostName} storniert`,
                        color: '#CE425D'
                    };
                } else if (cancelledByGuest) {
                    // Guest cancelled: "Du hast die Buchung storniert" Status: "Von dir storniert"
                    return {
                        message: `Du hast die Buchung storniert`,
                        statusText: 'Von dir storniert',
                        color: '#CE425D'
                    };
                } else if (cancelledBySystem) {
                    // System cancelled
                    return {
                        message: 'Die Buchung wurde automatisch storniert',
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                } else {
                    // Fallback if cancelledBy is not available - assume host cancelled
                    return {
                        message: `${hostName} hat die Buchung storniert`,
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                }
            }
        }

        // Default fallback
        return {
            message: '',
            statusText: 'Unbekannt',
            color: '#A1A7AE'
        };
    };

    const handleStarClick = (starIndex) => {
        setSelectedStars(starIndex + 1);
    };

    const handleNextClick = () => {
        if (selectedStars > 0) {
            setCurrentPage(2);
        }
    };

    const handleNextClickHost = () => {
        if (currentPage === 1) {
            // From page 1: need Studio & Communication stars
            if (selectedStudioStars > 0 && selectedCommunicationStars > 0) {
                setCurrentPage(2);
            }
        }
    };

    const handleStudioStarClick = (starIndex) => {
        setSelectedStudioStars(starIndex + 1);
    };

    const handleCommunicationStarClick = (starIndex) => {
        setSelectedCommunicationStars(starIndex + 1);
    };

    const handleBookingReviewSubmit = async () => {
        if (bookingReviewSubmitInFlightRef.current || bookingReviewSubmittedLocally) {
            return;
        }

        bookingReviewSubmitInFlightRef.current = true;
        try {
            const reviewData = {
                rating: selectedStudioStars,
                communicationRating: selectedCommunicationStars,
                content: studioReviewText || "",
                reviewerId: bookingData?.userID,            // Guest is reviewing
                revieweeId: bookingData?.Studio?.User?.id,  // Studio owner is being reviewed
                studioId: bookingData?.Studio?.id,
                bookingId: bookingData?.id
            };

            await submitStudioReview(reviewData);
            completeReviewSubmission(setBookingReviewSubmittedLocally);
        } catch (error) {
            if (isAlreadyReviewedError(error)) {
                completeReviewSubmission(setBookingReviewSubmittedLocally);
            } else {
                notifyErrorToast('Es gab ein Problem beim Absenden deiner Bewertung. Bitte versuche es später erneut.', {
                  title: 'Bewertung konnte nicht gesendet werden',
                });
            }
        } finally {
            bookingReviewSubmitInFlightRef.current = false;
        }
    };

    const handleGuestReviewSubmit = async () => {
        if (creatorReviewSubmitInFlightRef.current || creatorReviewSubmittedLocally) {
            return;
        }

        creatorReviewSubmitInFlightRef.current = true;
        setIsUserReviewSubmitting(true);

        try {
            const reviewData = {
                rating: selectedStars,
                content: guestReviewText || "",
                reviewerId: bookingData?.Studio?.User?.id,
                revieweeId: bookingData?.userID,
                studioId: bookingData?.Studio?.id,
                bookingId: bookingData?.id,
            };
            
            await submitUserReview(reviewData);
            completeReviewSubmission(setCreatorReviewSubmittedLocally);
        } catch (error) {
            if (isAlreadyReviewedError(error)) {
                completeReviewSubmission(setCreatorReviewSubmittedLocally);
            } else {
                notifyErrorToast('Es gab ein Problem beim Absenden deiner Bewertung. Bitte versuche es später erneut.', {
                  title: 'Bewertung konnte nicht gesendet werden',
                });
            }
        } finally {
            creatorReviewSubmitInFlightRef.current = false;
            setIsUserReviewSubmitting(false);
        }
    };


    const renderModalContent = () => {
        const { type, message, action, isRateBookingHostOpen, isRateHostOpen } = modal;

        if (type === 'error') {
            return (
                <>
                    <h3 style={{
                        color: 'var(--stu-rot)',
                        marginBottom: '15px',
                        fontSize: '18px',
                        fontWeight: '600'
                    }}>
                        Fehler aufgetreten
                    </h3>
                    <p style={{
                        color: '#333',
                        marginBottom: '20px',
                        fontSize: '14px',
                        lineHeight: '1.4'
                    }}>
                        {message}
                    </p>
                    <button 
                        onClick={closeModal}
                        style={{
                            backgroundColor: 'var(--stu-rot)',
                            color: '#fff',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        Verstanden
                    </button>
                </>
            );
        }

        if (type === 'confirm') {
            return (
                <div className={classes.modalContent}>
                    <h3 className="titleSemibold3">
                        {action === 'confirm' ? 'Buchung bestätigen?' : 'Buchung ablehnen?'}
                    </h3>
                    <div 
                        className={classes.closeButton} 
                        onClick={isBookingUpdateLoading ? undefined : closeModal}
                        style={{ cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer', opacity: isBookingUpdateLoading ? 0.5 : 1 }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                        </svg>
                    </div>
                    <p className={classes.modalText}>
                        {action === 'confirm' ? 'Mit dieser Auswahl bestätigst du diese Buchung.' : 
                        'Dieser Vorgang kann nicht mehr rückgängig gemacht werden.'}
                    </p>
                    <div className={classes.modalButtonsContainer}>
                        <button 
                            style={{
                                backgroundColor: isBookingUpdateLoading ? "var(--light-grey-2)" : "black", 
                                color: isBookingUpdateLoading ? "var(--dark-grey)" : "white",
                                cursor: isBookingUpdateLoading ? "not-allowed" : "pointer",
                                opacity: isBookingUpdateLoading ? 0.6 : 1
                            }}
                            className={`${classes.button} mediumLarge`}
                            onClick={handleModalAction}
                            disabled={isBookingUpdateLoading}
                        >
                            {isBookingUpdateLoading 
                                ? (action === 'confirm' ? 'Wird bestätigt...' : 'Wird abgelehnt...') 
                                : (action === 'confirm' ? 'Bestätigen' : 'Ablehnen')
                            }
                        </button>
                        <button 
                            style={{backgroundColor: "var(--light-grey-2)"}}
                            className={`${classes.button} mediumLarge`}
                            onClick={closeModal}
                            disabled={isBookingUpdateLoading}
                        >
                            Zurück
                        </button>
                        {action !== 'confirm' && (
                            <button 
                                className={classes.changeProposalButton}
                                disabled={isBookingUpdateLoading}
                            >
                                Änderung vorschlagen
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        if (type === 'rate' && !modal?.isRateBookingHostOpen) {
            return (
                <div className={classes.reviewsContainer}>
                    <div className={classes.headerContainer}>
                        <header className={classes.reviewsHeader}>
                            <h2 className={`regularXl ${classes.reviewsTitle} ${fromCreatorPage ? classes.fromCreatorPageTitle : ''}`}>Bewerte deinen Gast</h2>
                            <button onClick={closeModal} className={classes.reviewsCloseButton}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.62275 8.77149L0.113281 1.26202L1.10382 0.271484L8.61328 7.78095L16.1227 0.271484L17.1133 1.26202L9.60382 8.77149L17.1133 16.281L16.1227 17.2715L8.61328 9.76202L1.10382 17.2715L0.113281 16.281L7.62275 8.77149Z" fill="#1F1F1F"/>
                                </svg>
                            </button>
                                <button onClick={handleClose} className={classes.reviewsCloseButtonPhone}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.62275 8.77149L0.113281 1.26202L1.10382 0.271484L8.61328 7.78095L16.1227 0.271484L17.1133 1.26202L9.60382 8.77149L17.1133 16.281L16.1227 17.2715L8.61328 9.76202L1.10382 17.2715L0.113281 16.281L7.62275 8.77149Z" fill="#1F1F1F"/>
                                </svg>
                            </button>
                        </header>
                        <div className={classes.pageIndicatorContainer}>
                            <div style={{backgroundColor: 'var(--stu-rot)'}} className={classes.pageIndicator}></div>
                            <div style={{backgroundColor: currentPage >= 2 ? 'var(--stu-rot)' : ''}} className={classes.pageIndicator}></div>
                        </div>
                    </div>
                    {currentPage === 1 ? (
                        <div className={classes.reviewsInnerContainer}>
                            <div className={classes.userContainer}>
                                <div className={classes.userImageContainer}>
                                    <img src={bookingData?.User?.userProfilePicture || noUserImage} alt="user" />
                                </div>
                                <div className={classes.userInfoContainer}>
                                    <h2 className={`titleSemibold2 ${fromCreatorPage ? classes.fromCreatorPageTitle : ''}`}
                                    > {bookingData?.User?.firstName} {bookingData?.User?.lastName}</h2>
                                    {/* <p className="regularLarge">{bookingData?.Studio?.studioLocation}</p> */}
                                </div>
                            </div>
                            <div className={classes.longLine}></div>
                            <div className={classes.startReviewContainer}>
                                <h2 className="titleSemibold2">Bewerte deinen Gast.</h2>
                                <h3 className={`titleSemibold3 ${classes.phoneTitle}`}>Bewerte deinen Gast.</h3>
                                <div className={classes.starsContainer}>
                                    {[...Array(5)].map((_, index) => (
                                        <svg 
                                            key={index}
                                            onClick={() => handleStarClick(index)}
                                            style={{cursor: 'pointer'}}
                                            xmlns="http://www.w3.org/2000/svg" 
                                            width="42" 
                                            height="40" 
                                            viewBox="0 0 42 40"
                                            fill="none"
                                        >
                                            <g clipPath="url(#clip0_10926_15309)">
                                                <path d="M32.2487 29.5767L33.9688 39.6587L20.9961 32.8737L31.508 25.3003L32.0097 28.1911L32.2487 29.5767Z" fill={index < selectedStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                                <path d="M9.98635 28.1911L10.4881 25.3003L0 15.1468L14.5017 13.0444L21 0L27.4983 13.0444L42 15.1468L8.0273 39.6587L9.98635 28.1911Z" fill={index < selectedStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_10926_15309">
                                                    <rect width="42" height="39.6587" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    ))}
                                </div>
                            </div>
                            <div className={classes.phoneTextContainer}>
                                <p className="titleSemibold3">Teile deine Erfahrung mit {bookingData?.User?.firstName} (optional)</p>
                                <textarea 
                                    className={`${classes.reviewTextarea} slimRegularLarge`} 
                                    placeholder="Teile wichtige Details über die Erfahrung mit deinem Gast."
                                    defaultValue={guestReviewText}
                                    onBlur={(e) => setGuestReviewText(e.target.value)}
                                ></textarea>
                            </div>      
                        </div>
                    ) : currentPage === 2 ? (
                        <div className={classes.reviewsInnerContainerSecond}>
                            <h2 className="titleSemibold2">Teile deine Erfahrung mit {bookingData?.User?.firstName} (optional)</h2>
                            <textarea 
                                className={`${classes.reviewTextarea} slimRegularLarge`} 
                                placeholder={`Teile mit anderen wichtige Details über deine Erfahrung mit ${bookingData?.User?.firstName}.`}
                                defaultValue={guestReviewText}
                                onBlur={(e) => setGuestReviewText(e.target.value)}
                            ></textarea>
                        </div>
                    ) : (
                        <>
                        <div className={classes.reviewsInnerContainerSuccess}>
                            <div className={classes.successContent}>
                                <div className={classes.successContentIcon}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M31.6074 2.63723L9.69105 23.4831L9.69062 23.4827L9.68855 23.4847L0.392578 13.9887L2.48517 11.9402L9.76228 19.3739L29.5892 0.515381L31.6074 2.63723Z" fill="white"/>
                                    </svg>
                                </div>
                                <h2 className={`${classes.successContentTitle} titleSemibold2`}>Deine Bewertung wurde abgeschickt!</h2>
                            </div>
                            <p className="regularLarge" style={{marginTop: "16px"}}>
                                Danke für dein Feedback.
                            </p>
                        </div>
                    </>
                    )}
                    <footer className={classes.footerContainer} style={{justifyContent: currentPage === 3 ? 'flex-end' : 'space-between'}}>
                        {currentPage !== 3 && (
                            <button 
                                className={`${classes.backButton} ${classes.buttonCreatorPage} mediumLarge`}
                                onClick={() => {
                                    if (currentPage === 1) {
                                        closeModal();
                                    } else if (currentPage === 2) {
                                        setCurrentPage(1);
                                    }
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                                    <line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7"/>
                                    <line x1="0.626536" y1="8.547" x2="9.62508" y2="18.2954" stroke="#1F1F1F" strokeWidth="1.7"/>
                                </svg>
                                Zurück
                            </button>
                        )}
                        <>
                            <button 
                                className={`${classes.nextButton} ${classes.buttonCreatorPage} mediumLarge`}
                                onClick={currentPage === 2 ? handleGuestReviewSubmit : currentPage === 3 ? handleClose : handleNextClick}
                                disabled={(currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting}
                                style={{
                                    opacity: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 0.5 : 1,
                                    cursor: (currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {currentPage === 1 ? 'Weiter' : currentPage === 2 ? (isUserReviewSubmitting ? 'Wird gesendet...' : 'Abschicken') : 'Fertig'}
                            </button>
                            <button 
                                onClick={currentPage === 1 ? handleGuestReviewSubmit : handleClose}
                                className={`${classes.nextButtonPhone} regularLarge`}
                                disabled={(currentPage === 1 && selectedStars === 0) || isUserReviewSubmitting}
                                
                            >
                                 {currentPage === 1 ? (isUserReviewSubmitting ? 'Wird gesendet...' : 'Abschicken') : 'Fertig'}
                            </button>
                        </>
                    </footer>
                </div>
            );
        }

        if (type === 'rate' && modal?.isRateBookingHostOpen) {
            return (
                <div className={classes.reviewsContainer}>
                    <header className={classes.reviewsHeader}>
                        <h2 className={`titleSemibold3 ${classes.reviewsTitle}`}>Bewerte deine Buchung</h2>
                        <button onClick={handleClose} className={classes.reviewsCloseButton}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.62275 8.77149L0.113281 1.26202L1.10382 0.271484L8.61328 7.78095L16.1227 0.271484L17.1133 1.26202L9.60382 8.77149L17.1133 16.281L16.1227 17.2715L8.61328 9.76202L1.10382 17.2715L0.113281 16.281L7.62275 8.77149Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                        <button onClick={handleClose} className={classes.reviewsCloseButtonPhone}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.62275 8.77149L0.113281 1.26202L1.10382 0.271484L8.61328 7.78095L16.1227 0.271484L17.1133 1.26202L9.60382 8.77149L17.1133 16.281L16.1227 17.2715L8.61328 9.76202L1.10382 17.2715L0.113281 16.281L7.62275 8.77149Z" fill="#1F1F1F"/>
                            </svg>
                        </button>
                    </header>
                    <div className={classes.pageIndicatorContainer}>
                        <div style={{backgroundColor: 'var(--stu-rot)'}} className={classes.pageIndicator}></div>
                        <div style={{backgroundColor: currentPage >= 2 ? 'var(--stu-rot)' : ''}} className={classes.pageIndicator}></div>
                    </div>
                    {!fromCreatorPage && (
                            <div className={classes.pageIndicatorContainerPhone}>
                                <div style={{backgroundColor: 'var(--stu-rot)'}} className={classes.pageIndicator}></div>
                                <div style={{backgroundColor: currentPage >= 2 ? 'var(--stu-rot)' : ''}} className={classes.pageIndicator}></div>
                            </div>
                    )}
                    {currentPage === 1 ? (
                        <div className={classes.reviewsInnerContainer}>
                            <div className={classes.userContainer} style={{borderTop: !fromCreatorPage ? '0px' : '', marginBottom: !fromCreatorPage ? '6px' : ''}}>
                                <div className={classes.bookingImageContainer}>
                                    <img className={classes.bookingImage} src={bookingData?.Studio?.processedImages?.[0]} alt="user" />
                                    <div className={classes.bookingImageContainerInner}>
                                        <img className={classes.userImageBookingRating} src={bookingData?.Studio?.User?.studioOwnerProfilePicture} alt="user" />
                                    </div>
                                </div>
                                <div className={classes.userInfoContainer}>
                                    <h2 className={`titleSemibold3 ${classes.studioNameTitle}`}>
                                        {bookingData?.Studio?.StudioName}
                                    </h2>
                                    <h2 className="semiboldLarge">Host: {bookingData?.Studio?.User?.firstName} {bookingData?.Studio?.User?.lastName}</h2>
                                    <p className="regularLarge">{coarseAddressText}</p>
                                </div>
                            </div>
                            <div className={classes.longLine}></div>
                            <div className={classes.startHostReviewContainer}>
                                <h2 className="titleSemibold2">Wie war das Studio & die Ausstattung?</h2>
                                <div className={classes.starsContainer}>
                                    {[...Array(5)].map((_, index) => (
                                        <svg 
                                            key={index}
                                            onClick={() => handleStudioStarClick(index)}
                                            style={{cursor: 'pointer'}}
                                            xmlns="http://www.w3.org/2000/svg" 
                                            width="42" 
                                            height="40" 
                                            viewBox="0 0 42 40"
                                            fill="none"
                                        >
                                            <g clipPath="url(#clip0_10926_15309)">
                                                <path d="M32.2487 29.5767L33.9688 39.6587L20.9961 32.8737L31.508 25.3003L32.0097 28.1911L32.2487 29.5767Z" fill={index < selectedStudioStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                                <path d="M9.98635 28.1911L10.4881 25.3003L0 15.1468L14.5017 13.0444L21 0L27.4983 13.0444L42 15.1468L8.0273 39.6587L9.98635 28.1911Z" fill={index < selectedStudioStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_10926_15309">
                                                    <rect width="42" height="39.6587" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    ))}
                                </div>
                                <h2 className="titleSemibold2">Wie war die Kommunikation mit {bookingData?.Studio?.User?.firstName}?</h2>
                                <div className={classes.starsContainer}>
                                    {[...Array(5)].map((_, index) => (
                                        <svg 
                                            key={index}
                                            onClick={() => handleCommunicationStarClick(index)}
                                            style={{cursor: 'pointer'}}
                                            xmlns="http://www.w3.org/2000/svg" 
                                            width="42" 
                                            height="40" 
                                            viewBox="0 0 42 40"
                                            fill="none"
                                        >
                                            <g clipPath="url(#clip0_10926_15309)">
                                                <path d="M32.2487 29.5767L33.9688 39.6587L20.9961 32.8737L31.508 25.3003L32.0097 28.1911L32.2487 29.5767Z" fill={index < selectedCommunicationStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                                <path d="M9.98635 28.1911L10.4881 25.3003L0 15.1468L14.5017 13.0444L21 0L27.4983 13.0444L42 15.1468L8.0273 39.6587L9.98635 28.1911Z" fill={index < selectedCommunicationStars ? 'var(--stu-rot)' : '#E2E8EF'}/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_10926_15309">
                                                    <rect width="42" height="39.6587" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : currentPage === 2 ? (
                        <div className={classes.reviewsInnerContainerSecond}>
                            <h2 className="titleSemibold2">Wie war deine Studio-Erfahrung?</h2>
                            <textarea 
                                className={`${classes.reviewTextarea} slimRegularLarge`} 
                                placeholder="Teile mit anderen wichtige Details über deine Studio-Erfahrung..."
                                defaultValue={studioReviewText}
                                onBlur={(e) => setStudioReviewText(e.target.value)}
                            ></textarea>
                        </div>
                    ) : currentPage === 3 ? (
                        <>
                        <div 
                            className={classes.reviewsInnerContainerSuccess} 
                            // style={{paddingBottom: !moreReviews ? '125px' : '52px'}}
                        >
                            <div className={classes.successContent}>
                                <div className={classes.successContentIcon}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="24" viewBox="0 0 32 24" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M31.6074 2.63723L9.69105 23.4831L9.69062 23.4827L9.68855 23.4847L0.392578 13.9887L2.48517 11.9402L9.76228 19.3739L29.5892 0.515381L31.6074 2.63723Z" fill="white"/>
                                    </svg>
                                </div>
                                <h2 className={`${classes.successContentTitle} titleSemibold2`}>Deine Bewertung wurde abgeschickt!</h2>
                            </div>
                            <p className="regularLarge">Danke für dein Feedback.</p>
                            {/* {moreReviews && (
                                <button className={`${classes.moreReviewsButton} mediumLarge`}>
                                    Bewertung über dich anschauen
                                </button>
                            )} */}
                        </div>
                        {/* {moreReviews && (
                            <button className={`${classes.moreReviewsButtonPhone} mediumLarge`}>
                                Bewertung über dich anschauen
                            </button>
                        )} */}
                        </>
                    ) : null}
                    <footer className={classes.footerContainer} style={{justifyContent: currentPage === 3 ? 'flex-end' : 'space-between'}}>
                        {currentPage !== 3 && (
                            <button 
                                className={`${classes.backButton} mediumLarge`}
                                onClick={() => {
                                    if (currentPage === 1) {
                                        closeModal();
                                    } else if (currentPage === 2) {
                                        setCurrentPage(1);
                                    }
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                                    <line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7"/>
                                    <line x1="0.626536" y1="8.547" x2="9.62508" y2="18.2954" stroke="#1F1F1F" strokeWidth="1.7"/>
                                </svg>
                                Zurück
                            </button>
                        )}
                        <button 
                            className={`${classes.nextButton} mediumLarge`}
                            onClick={() => {
                                if (currentPage === 2) {
                                    handleBookingReviewSubmit();
                                } else if (currentPage === 3) {
                                    handleClose();
                                } else {
                                    handleNextClickHost();
                                }
                            }}
                            disabled={
                                isStudioReviewSubmitting || 
                                (currentPage === 1 && (selectedStudioStars === 0 || selectedCommunicationStars === 0))
                            }
                            style={{
                                width: currentPage === 3 && isMobile ? '100%' : 'auto',
                                opacity: (
                                    isStudioReviewSubmitting || 
                                    (currentPage === 1 && (selectedStudioStars === 0 || selectedCommunicationStars === 0))
                                ) ? 0.5 : 1,
                                cursor: (
                                    isStudioReviewSubmitting || 
                                    (currentPage === 1 && (selectedStudioStars === 0 || selectedCommunicationStars === 0))
                                ) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {currentPage === 1 ? 'Weiter' : currentPage === 2 ? (isStudioReviewSubmitting ? 'Wird gesendet...' : 'Abschicken') : 'Fertig'}
                        </button>
                    </footer>
                </div>
            )
        }

        return null;
    };

    if (loading || !bookingData) {
        return <BookingDetailsCardSkeleton fromCreatorPage={fromCreatorPage} />;
    }

    return (
        <>
            <div 
                style={{
                    paddingTop: fromCreatorPage || fromMessagesPage ? "0px" : "30px", 
                    height: fromMessagesPage ? "auto" : "100%",
                    paddingBottom: fromMessagesPage ? "0px" : "90px",
                }} 
                className={classes.container}
            >
                <div 
                    className={classes.content}
                    style={{
                        borderRadius: fromMessagesPage ? "0px" : "10px",
                        border: fromMessagesPage ? "0" : "1px solid var(--light-grey-1)",
                        width: fromMessagesPage ? "100%" : "auto",
                        paddingBottom: fromMessagesPage ? "0px" : "25px",
                    }}
                >
                    <Link to={`/studio/${bookingData?.Studio?.id}`}>
                        <div className={classes.studioInfoContainer}>
                            <div className={`${classes.innerContainer} ${classes.image}`}>
                                <div className={classes.imageContainer}>
                                    <img src={bookingData?.Studio?.processedImages?.[0] || noImage} alt={bookingData?.Studio?.StudioName} />
                                </div>
                                <div className={classes.textContainer}>
                                    <span className="mediumLarge">{bookingData?.Studio?.StudioName}</span>
                                    <p className="regularXxs">{coarseAddressText}</p>
                                </div>
                            </div>
                        </div>
                        {packageData && (
                        <div className={classes.packageInfoContainerWrapper}>
                            <div className={classes.packageInfoContainer}>
                                <div className={classes.packageInfoContainerImage}>
                                    <img
                                        src={
                                            (fromCreatorPage
                                                ? packageData?.firstPortfolioImage
                                                : packageData?.processedPortfolioImages?.[0]) || noImage
                                        }
                                        alt={packageData?.name}
                                    />
                                </div>
                            <div className={classes.packageInfoContainerText}>
                                <span className="semiboldXs">{packageData?.name}</span>
                                {/* <p style={{color: 'var(--dark-grey-3)'}} className="regularXs">{packageData?.description}</p> */}
                            </div>
                            </div>
                        </div>
                        )}
                    </Link>
                    <div className={classes.horizontalLine} 
                        style={{
                            height: fromMessagesPage ? "11px" : "1px",
                            background: fromMessagesPage ? "var(--light-grey-2)" : "var(--light-grey-1)",
                        }}
                    />
                    <div className={classes.contentContainer}>
                        {(() => {
                            const statusDisplay = getStatusDisplay();
                            return (
                                <>
                                    {statusDisplay.message && (
                                        <p style={{marginBottom: '10px', color: 'var(--dark-grey-3)'}} className="regularLarge">
                                            {statusDisplay.message}
                                        </p>
                                    )}
                                    <span className="semiboldMedium">Status</span>
                                    <p style={{color: statusDisplay.color}} className="semiboldLarge">
                                        {statusDisplay.statusText}
                                    </p>
                                </>
                            );
                        })()}
                    </div>
                    <div className={classes.horizontalLine}
                        style={{
                            height: fromMessagesPage ? "11px" : "1px",
                            background: fromMessagesPage ? "var(--light-grey-2)" : "var(--light-grey-1)",
                        }}
                    />
                    <div className={`${classes.innerContainer} ${classes.dateContainer}`}>
                        <span className="semiboldMiddle">Datum und Zeit</span>
                        <div className={classes.dateTextContainer}>
                            <p className="regularLarge">
                                {formatDateTime(bookingData?.startDateTime, bookingData?.endDateTime).date}
                            </p>
                            <p className="regularLarge">
                                {formatDateTime(bookingData?.startDateTime, bookingData?.endDateTime).time}
                            </p>
                            <div className={`${classes.timeContainer} regularLarge`}>
                                <p className="regularXxs">
                                    Dauer: {calculateDuration(bookingData?.startDateTime, bookingData?.endDateTime)} Stunden
                                </p>
                            </div>
                        </div>
                        <button onClick={generateICSFile} className={classes.exportToCalendar}>
                            In Kalender exportieren
                        </button>
                    </div>
                    {(!fromCreatorPage || fromMessagesPage) && (
                        <>
                            <div className={classes.horizontalLine}
                                style={{
                                    height: fromMessagesPage ? "11px" : "1px",
                                    background: fromMessagesPage ? "var(--light-grey-2)" : "var(--light-grey-1)",
                                }}
                            />
                            <div className={`${classes.innerContainer} ${classes.addressContainer}`}> 
                                <span className="semiboldMiddle">Addresse</span>
                                <p className="regularLarge">{addressText || 'Der genaue Standort wird nach bestätigter Buchung mitgeteilt.'}</p>
                                {hasExactLocation ? (
                                    <button onClick={openGoogleMaps} className={classes.exportToCalendar}>Wegbeschreibung</button>
                                ) : null}
                            </div>
                        </>
                    )}
                    <div className={classes.horizontalLine}
                        style={{
                            height: fromMessagesPage ? "11px" : "1px",
                            background: fromMessagesPage ? "var(--light-grey-2)" : "var(--light-grey-1)",
                        }}
                    />
                    {!fromMessagesPage && (
                    <div className={`${classes.innerContainer} ${classes.participantsContainer}`}>
                        <span className="semiboldMiddle">Teilnehmer</span>
                        <p className="regularLarge">{bookingData?.Studio?.numberOfPeople} Personen</p>
                    </div>
                    )}
                </div>
                {!suppressInlineActions && fromCreatorPage && (bookingData?.status === "PENDING") && !hasBookingTimePassed() && (
                    <div className={classes.buttonsContainer}>
                        <button 
                            onClick={() => openModal('confirm', { action: 'confirm' })} 
                            className={`${classes.button} mediumLarge`} 
                            style={{backgroundColor: "#000", color: "#FFF"}}
                        >
                            Bestätigen
                        </button>
                        <button 
                            onClick={() => openModal('confirm', { action: 'decline' })}
                            className={`${classes.button} mediumLarge`} 
                            style={{backgroundColor: "var(--light-grey-2)"}}
                        >
                            Ablehnen
                        </button>
                    </div>
                )}
                {!suppressInlineActions && fromCreatorPage && (bookingData?.status === "COMPLETED") && hasBookingTimePassed() && !hasExistingReview && !reviewsLoading && (
                    <div className={classes.rateButtonContainer}>
                        <button onClick={() => openModal('rate', { isRateBookingHostOpen: false })} className={`${classes.rateButton} regularMedium`}>
                            Bewerte deinen Gast
                        </button>
                    </div>
                )}
                {!suppressInlineActions && !fromCreatorPage && (bookingData?.status === "COMPLETED") && hasBookingTimePassed() && !hasExistingReview && !reviewsLoading && (
                    <div className={classes.rateButtonContainer}>
                        <button onClick={() => openModal('rate', { isRateBookingHostOpen: true })} className={`${classes.rateButton} regularMedium`}>
                            Bewerte deine Buchung
                        </button>
                    </div>
                )}
            </div>
            <div className={classes.rateButtonContainerPhone}>
                {!suppressInlineActions && fromCreatorPage && (bookingData?.status === "COMPLETED") && hasBookingTimePassed() && !hasExistingReview && !reviewsLoading && (
                    <button 
                        onClick={() => openModal('rate', { isRateBookingHostOpen: false })} 
                        className={`${classes.rateButton} regularMedium`}
                    >
                        Bewerte deinen Gast
                    </button>
                )}
                {!suppressInlineActions && !fromCreatorPage && (bookingData?.status === "COMPLETED") && hasBookingTimePassed() && !hasExistingReview && !reviewsLoading && (
                    <button 
                        onClick={() => openModal('rate', { isRateBookingHostOpen: true })} 
                        className={`${classes.rateButton} regularMedium`}
                    >
                        Bewerte deine Buchung
                    </button>
                )}
            </div>
            
            {!suppressInlineActions && !isMobile && (
                <SmallModal 
                    open={modal?.isOpen} 
                    onBackdropClick={isBookingUpdateLoading ? undefined : closeModal}
                    reviewModal={modal?.type === 'rate'}
                    fromBookingDetails={true}
                >
                    {renderModalContent()}
                </SmallModal>
            )}
            
            {!suppressInlineActions && isMobile && (
                <div className={`${classes.mobileModalContainer} ${modal?.isOpen ? classes.mobileModalOpen : ''}`}>
                    <div className={classes.mobileModalContent}>
                        {renderModalContent()}
                    </div>
                </div>
            )}
            {!suppressInlineActions && (
            <SaveNotification 
                show={showNotification} 
                onHide={() => setShowNotification(false)}
                message={notificationMessage}
            />
            )}
        </>
    );
};

export default BookingDetailsCard;
